import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, TrendingDown, TrendingUp, Wallet, FileWarning, Plus, ReceiptIndianRupee } from "lucide-react";
import { api } from "@/lib/api";
import { inr, compactInr, fmtDate, DOC_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-56" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  const m = data.this_month || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold tracking-tight">Today</h1>
          <p className="text-sm text-slate-500 mt-1">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="quick-payment" onClick={() => navigate("/payments?new=1")} className="gap-1.5 rounded-full">
            <Wallet size={15} /> Record Payment
          </Button>
          <Button size="sm" data-testid="quick-invoice" onClick={() => navigate("/invoices/new")} className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
            <Plus size={15} /> Invoice
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          testid="kpi-to-collect" label="To Collect" accent="rose"
          value={inr(data.to_collect?.amount)} sub={`${data.to_collect?.count || 0} open invoices`}
          onClick={() => navigate("/reports?tab=ageing")} icon={ReceiptIndianRupee}
        />
        <Kpi
          testid="kpi-overdue" label="Overdue" accent="red"
          value={inr(data.overdue?.amount)} sub={`${data.overdue?.count || 0} past due date`}
          onClick={() => navigate("/reports?tab=ageing")} icon={FileWarning}
        />
        <Kpi
          testid="kpi-cash-bank" label="Cash & Bank (net collected)" accent="emerald"
          value={compactInr(data.cash_bank?.amount)} sub="Cleared receipts less payouts"
          onClick={() => navigate("/payments")} icon={Wallet}
        />
        <Kpi
          testid="kpi-month-sales" label="This Month Sales" accent="blue"
          value={compactInr(m.sales)}
          sub={m.change_pct == null ? "No sales last month" : `${m.change_pct > 0 ? "+" : ""}${m.change_pct}% vs last month`}
          trend={m.change_pct}
          onClick={() => navigate("/reports?tab=sales")} icon={TrendingUp}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2 rounded-md border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
            <h2 className="text-base font-semibold">Recent documents</h2>
            <Link to="/invoices" data-testid="see-all-invoices"
              className="-mr-2 inline-flex min-h-[40px] items-center gap-1 rounded-md px-2 text-xs text-blue-700 hover:text-blue-900">
              See all <ArrowUpRight size={13} />
            </Link>
          </div>
          {/* On a phone the five-column table clipped the amount — the one
              column anyone opens the dashboard to see. Below md the same rows
              render as a stacked list instead. */}
          <div className="divide-y divide-slate-100 md:hidden" data-testid="recent-documents-list">
            {(data.recent_documents || []).map((d) => (
              <button key={d.id} type="button" onClick={() => navigate(`/invoices/${d.id}`)}
                data-testid={`recent-doc-m-${d.id}`}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 active:bg-slate-50">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">{d.party_name}</span>
                  <span className="mt-0.5 block truncate font-mono text-[11px] text-slate-500">
                    {d.document_number || "DRAFT"} · {fmtDate(d.document_date)}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-mono text-sm font-semibold tabular-nums">{inr(d.grand_total)}</span>
                  <span className="mt-1 block"><StatusBadge status={d.payment_status} /></span>
                </span>
              </button>
            ))}
            {!(data.recent_documents || []).length && (
              <div className="px-5 py-10 text-center text-sm text-slate-500">No documents yet</div>
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm" data-testid="recent-documents-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-2 text-left font-semibold">Number</th>
                  <th className="px-3 py-2 text-left font-semibold">Party</th>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="px-5 py-2 text-right font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.recent_documents || []).map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => navigate(`/invoices/${d.id}`)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors duration-150"
                    data-testid={`recent-doc-${d.id}`}
                  >
                    <td className="px-5 py-2.5 font-mono text-xs font-semibold">{d.document_number || "DRAFT"}</td>
                    <td className="px-3 py-2.5 truncate max-w-[180px]">{d.party_name}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{fmtDate(d.document_date)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(d.grand_total)}</td>
                    <td className="px-5 py-2.5 text-right"><StatusBadge status={d.payment_status} /></td>
                  </tr>
                ))}
                {!(data.recent_documents || []).length && (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-500">No documents yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-md border border-slate-200 bg-white">
            <div className="px-5 py-3.5 border-b border-slate-200">
              <h2 className="text-base font-semibold">Needs attention</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {(data.action_list || []).map((a) => (
                <div key={a.code} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`action-${a.code}`}>
                  <div className="text-sm text-slate-700">{a.label}</div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-semibold">{a.count}</div>
                    {a.amount ? <div className="font-mono text-[11px] text-slate-500">{compactInr(a.amount)}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-900 text-white p-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-400 font-semibold">This month</div>
            <dl className="mt-4 space-y-3 text-sm">
              <Line label="Sales" value={inr(m.sales)} />
              <Line label="Collected" value={inr(m.collected)} />
              <Line label="Credit notes" value={inr(m.credit_notes)} />
              <Line label="GST charged" value={inr(m.tax)} highlight />
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

const Line = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between">
    <dt className="text-slate-400">{label}</dt>
    <dd className={`font-mono tabular-nums ${highlight ? "text-blue-400 font-semibold" : ""}`}>{value}</dd>
  </div>
);

const ACCENT = {
  rose: "text-rose-600 bg-rose-50",
  red: "text-red-700 bg-red-50",
  emerald: "text-emerald-700 bg-emerald-50",
  blue: "text-blue-700 bg-blue-50",
};

const Kpi = ({ label, value, sub, accent, onClick, testid, icon: Icon, trend }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left rounded-md border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-sm transition-[border-color,box-shadow] duration-150"
  >
    <div className="flex items-start justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <span className={`rounded p-1.5 ${ACCENT[accent]}`}><Icon size={14} /></span>
    </div>
    <div className="mt-3 font-mono text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
      {trend != null && (trend >= 0 ? <TrendingUp size={12} className="text-emerald-600" /> : <TrendingDown size={12} className="text-rose-600" />)}
      {sub}
    </div>
  </button>
);
